/**
 * API Key Step Component
 *
 * Step 2: Display the newly created API key with copy functionality.
 *
 * @module components/onboarding/ApiKeyStep
 */

import { useState } from 'react';
import { Button, Alert } from '@/components/ui';
import { Copy, Check, Key, ArrowRight } from 'lucide-react';

// =============================================================================
// Types
// =============================================================================

export interface ApiKeyStepProps {
  /** The full API key to display */
  apiKey: string;
  /** Callback when user continues */
  onContinue: () => void;
}

// =============================================================================
// Component
// =============================================================================

export function ApiKeyStep({ apiKey, onContinue }: ApiKeyStepProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(apiKey);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-display font-semibold text-gray-900">
          🥳 Your API key is ready
        </h2>
        <p className="text-gray-500 mt-2">
          We've created your first live API key. Copy it now - you won't see it
          again.
        </p>
      </div>

      {/* Warning */}
      <Alert variant="warning" title="Save this key securely">
        This is the only time you'll see the full key. Store it in a
        secure location like a password manager or environment variable.
      </Alert>

      {/* Key display */}
      <div className="space-y-3">
        <label className="block text-sm font-medium text-gray-700">
          Your API Key
        </label>
        <div className="relative">
          <div className="flex items-center gap-2 p-4 bg-gray-900 rounded-lg font-mono text-sm">
            <Key className="h-4 w-4 text-gray-400 flex-shrink-0" />
            <code className="text-green-400 flex-1 break-all">{apiKey}</code>
            <Button
              variant="secondary"
              size="sm"
              onClick={handleCopy}
              leftIcon={
                copied ? (
                  <Check className="h-4 w-4 text-success-500" />
                ) : (
                  <Copy className="h-4 w-4" />
                )
              }
              className="flex-shrink-0"
            >
              {copied ? 'Copied!' : 'Copy'}
            </Button>
          </div>
        </div>
      </div>

      <Button
        onClick={onContinue}
        rightIcon={<ArrowRight className="h-4 w-4" />}
      >
        I've saved my key
      </Button>
    </div>
  );
}
